/**
 * A− / A+ for Docs: steps the whole frame’s font
 * size, stored in `metadata.font_size`. The change shows at once; a failed
 * save rolls back to the last saved size and explains why.
 */
import { useCallback, useRef } from "react";
import { updateFrame } from "@/api/spaces";
import { useFramesStore } from "@/stores/frames-store";
import { useNotificationStore } from "@/stores/notification-store";
import { useProjectStore } from "@/stores/project-store";
import {
  normalizeDocFontSize,
  stepDocFontSize,
} from "../doc-frame/doc-font-size";
import { changeFrameMetadata } from "@/lib/frame-detail-requests";
import { applyFramePatch } from "@/lib/frame-ingest";
import { translate } from "@/i18n";
import { framesNoteMessages } from "@/i18n/messages/frames-note";

export function useFrameFontSize(
  frameId: string,
  savedFontSize: unknown,
): (direction: 1 | -1) => void {
  const saveQueueRef = useRef(Promise.resolve());
  const savedFontSizeRef = useRef(savedFontSize);

  return useCallback(
    (direction: 1 | -1) => {
      const projectId = useProjectStore.getState().currentProject?.id;
      if (!projectId) return;
      const stepped = (metadata: Record<string, unknown>) => {
        const current = normalizeDocFontSize(metadata.font_size);
        const next = stepDocFontSize(current, direction);
        return next === current ? null : { ...metadata, font_size: next };
      };
      // The store's updateFrame replaces metadata wholesale: save it merged.
      changeFrameMetadata(frameId, stepped, (metadata) => {
        const next = metadata.font_size;
        saveQueueRef.current = saveQueueRef.current.then(async () => {
          try {
            await updateFrame(projectId, frameId, { metadata });
            savedFontSizeRef.current = next;
          } catch {
            const latest = useFramesStore.getState().framesById[frameId];
            if (latest && latest.metadata?.font_size === next) {
              const rollback = { ...latest.metadata };
              if (savedFontSizeRef.current !== undefined)
                rollback.font_size = savedFontSizeRef.current;
              else delete rollback.font_size;
              applyFramePatch(frameId, { metadata: rollback });
            }
            useNotificationStore.getState().addSystemToast({
              id: `note-font-size-save-error-${frameId}`,
              title: translate(framesNoteMessages, "saveSizeFailedTitle"),
              description: translate(framesNoteMessages, "saveSizeFailedBody"),
              level: "error",
            });
          }
        });
      });
    },
    [frameId],
  );
}
